import React from 'react';
import { Clock, XCircle, Package, PoundSterling, TrendingUp, Coins } from 'lucide-react';
import { orderCostSummary } from '@/lib/costMargin';

// Quick KPI strip for the orders dashboard: pending, failed payments,
// awaiting dispatch and today's revenue, plus the COGS and gross margin made on
// the sales currently in view.
const PAID_STATUSES = ['paid', 'completed', 'dispatched', 'delivered', 'in_production', 'packed'];

function isToday(d) {
  const t = new Date(d);
  const now = new Date();
  return t.getDate() === now.getDate() && t.getMonth() === now.getMonth() && t.getFullYear() === now.getFullYear();
}

export default function OrdersKpiStrip({ orders = [], marginOrders }) {
  const pending = orders.filter((o) => o.status === 'pending').length;
  const failed = orders.filter((o) => o.status === 'failed').length;
  const awaitingDispatch = orders.filter((o) => ['in_production', 'packed'].includes(o.status)).length;
  const todayRevenue = orders
    .filter((o) => PAID_STATUSES.includes(o.status) && o.created_date && isToday(o.created_date))
    .reduce((s, o) => s + Number(o.amount || 0), 0);

  const cards = [
    { label: 'PENDING', value: pending, icon: Clock, color: 'text-yellow-400' },
    { label: 'FAILED', value: failed, icon: XCircle, color: 'text-red-400' },
    { label: 'AWAITING DISPATCH', value: awaitingDispatch, icon: Package, color: 'text-blue-400' },
    { label: "TODAY'S REVENUE", value: `£${todayRevenue.toFixed(2)}`, icon: PoundSterling, color: 'text-green-400' },
  ];

  // Margin is worked out over the sales in view, and only over those that
  // actually carry a cost — a sale with none would otherwise read as pure profit.
  const summaries = (marginOrders || orders)
    .filter((o) => PAID_STATUSES.includes(o.status))
    .map(orderCostSummary);
  const costed = summaries.filter((s) => s.cogs != null);
  const totalNet = costed.reduce((s, x) => s + x.net, 0);
  const totalCogs = costed.reduce((s, x) => s + x.cogs, 0);
  const totalMargin = totalNet - totalCogs;
  const marginPct = totalNet > 0 ? (totalMargin / totalNet) * 100 : null;
  const withoutCost = summaries.length - costed.length;

  const marginCards = [
    { label: 'COGS', value: `£${totalCogs.toFixed(2)}`, icon: Coins, color: 'text-white/60' },
    { label: 'GROSS MARGIN', value: `£${totalMargin.toFixed(2)}`, icon: TrendingUp, color: 'text-green-400' },
    { label: 'MARGIN %', value: marginPct == null ? '—' : `${marginPct.toFixed(0)}%`, icon: TrendingUp, color: 'text-primary' },
  ];

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="border border-white/5 bg-[#111115] px-4 py-3.5">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-white/30 text-[9px] font-bold tracking-widest">{c.label}</p>
                <Icon className={`w-4 h-4 ${c.color}`} />
              </div>
              <p className="text-white font-black text-xl">{c.value}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        {marginCards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="border border-white/5 bg-[#111115] px-4 py-3.5">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-white/30 text-[9px] font-bold tracking-widest">{c.label}</p>
                <Icon className={`w-4 h-4 ${c.color}`} />
              </div>
              <p className={`font-black text-xl ${c.color}`}>{c.value}</p>
            </div>
          );
        })}
      </div>

      {withoutCost > 0 && (
        <p className="text-white/25 text-[11px] -mt-4 mb-6">
          {withoutCost} sale{withoutCost !== 1 ? 's' : ''} in view {withoutCost !== 1 ? 'have' : 'has'} no cost recorded and {withoutCost !== 1 ? 'are' : 'is'} excluded from the margin figures above.
        </p>
      )}
    </>
  );
}